import graphql from "babel-plugin-relay/macro";
import { useCallback, useState } from "react";
import { Platform, Pressable } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useFragment } from "react-relay";

import { Menu } from "@whoosh/design-system/src/components/overlay/Menu";
import {
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { MemberListModal } from "../../../MemberListModal";
import { useGetTabBarItems } from "../../hooks/useGetTabBarItems";
import { useShouldHideNav } from "../../hooks/useShouldHideNav";
import { RouteLookupNavigation } from "../../utils/getNavigationForRoute";
import { BookButton } from "../BookButton";
import { TabBarOverflowItem } from "../TabBarOverflowItem";
import { TabBarNav_session$key } from "./__generated__/TabBarNav_session.graphql";
import { TabBarItem } from "./components/TabBarItem";
import { TabBarMoreItem } from "./components/TabBarMoreItem";
import { getTabBarBottomOffset } from "./getTabBarBottomOffset";

type Props = {
  navigation: RouteLookupNavigation;
  sessionRef: TabBarNav_session$key;
};

/** this is the mobile menu */
export const TabBarNav = ({ navigation, sessionRef }: Props) => {
  const session = useFragment(TabBarNavFragmentString, sessionRef);

  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);

  const openMemberModal = useCallback(() => {
    setIsMemberModalOpen(true);
  }, []);
  const closeMemberModal = useCallback(() => {
    setIsMemberModalOpen(false);
  }, []);
  const { items, overflowItems } = useGetTabBarItems({
    navigationScope: "clubStack",
    navigationOverride: navigation,
    openMemberModal,
    sessionRef: session,
  });

  const hideNav = useShouldHideNav();
  /**
   * Web needs additional room for browser chrome. Android needs the measured safe area
   * because edge-to-edge windows extend behind system navigation.
   */
  const insets = useSafeAreaInsets();
  const bottomOffset = getTabBarBottomOffset({
    bottomInset: insets.bottom,
    platform: Platform.OS,
  });
  const isMobileScreen = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    agendaSplit: true,
    eventCardColumn: true,
    lg: false,
    xl: false,
  });

  return !isMobileScreen || hideNav ? null : (
    <Box
      p={4}
      position="absolute"
      bottom={bottomOffset}
      right={0}
      left={0}
      justifyContent="center"
      alignItems="center"
      height={"auto"}
      pointerEvents={Platform.OS === "web" ? "none" : "auto"}
    >
      <HStack
        maxWidth="100%"
        width="auto"
        borderRadius="6"
        p={{ base: 2, lg: 6 }}
        bg="surface-primary"
        position="relative"
        pointerEvents="auto"
        justifyContent="center"
        style={{
          shadowOffset: { width: 0, height: 8 },
          shadowRadius: 24,
          shadowOpacity: 0.08,
          shadowColor: "#000",
        }}
      >
        {items.map((item, i) => (
          <TabBarItem
            key={i}
            routeName={item.routeName}
            selected={item.selected}
            label={item.label}
            onPress={() => {
              item.routeHandler();
            }}
            icon={(props) => <item.icon {...props} />}
          />
        ))}

        {overflowItems.length ? (
          <Menu
            openPosition="top-end"
            verticalOffset={16}
            showActionSheetDoneButton
            trigger={({ onPress, isOpen }) => (
              <Pressable
                onPress={onPress}
                accessibilityRole="button"
                accessibilityState={{ expanded: isOpen }}
              >
                <TabBarMoreItem />
              </Pressable>
            )}
            content={({ closeMenu }) => (
              <Menu.Content size="regular">
                <VStack accessibilityRole="menu">
                  {overflowItems.map((item, i) =>
                    item.isDivider ? (
                      <Box
                        key={i}
                        height={1}
                        bg="surface-dividers"
                        marginVertical={1}
                      />
                    ) : (
                      <TabBarOverflowItem
                        key={i}
                        label={item.label}
                        selected={item.selected}
                        onPress={() => {
                          closeMenu(item.routeHandler);
                        }}
                        icon={(props) => <item.icon {...props} />}
                      />
                    )
                  )}
                </VStack>
              </Menu.Content>
            )}
          />
        ) : null}

        <BookButton sessionRef={session} />
      </HStack>
      <MemberListModal isOpen={isMemberModalOpen} onClose={closeMemberModal} />
    </Box>
  );
};

const TabBarNavFragmentString = graphql`
  fragment TabBarNav_session on Session {
    ...useGetTabBarItemsFragment_session
    ...BookButton_session
  }
`;
